<script lang="ts" setup>
import type { SpatialData } from '@/types/spatialData'

import { ElMessage } from 'element-plus'
import { reactive, watch } from 'vue'

import SettingsEditor from '@/components/SettingsEditor.vue'
import SpatialDataUploader from '@/components/SpatialDataUploader.vue'
import { useCharecterStore } from '@/stores/store'
import { BrainPlatform } from '@/types/brain'
import { ElevenLabsModel, FishAudioLatency, VoicePlatform } from '@/types/voice'

const characterStore = useCharecterStore()
const voicePlatform = Object.values(VoicePlatform)
const brainPlatform = Object.values(BrainPlatform)
// do not use same name with ref
const form = reactive(characterStore.currentCharecter)

watch(() => form.voice.platform, (newPlatform, oldPlatform) => {
  console.log('~~~~~~~', newPlatform, oldPlatform)
  if (newPlatform === VoicePlatform.VOLC) {
    form.voice.settings = {
      speaker: 'zh_female_wanwanxiaohe_moon_bigtts',
      speechRate: 0,
      pitchRate: 0,
    }
  }
  else if (newPlatform === VoicePlatform.ELEVENLABS) {
    form.voice.settings = {
      voiceId: 'iP95p4xoKVk53GoZ742B',
      model: ElevenLabsModel.ELEVEN_TURBO_V2_5,
      stability: 0.5,
      similarityBoost: 0.5,
    }
  }
  else if (newPlatform === VoicePlatform.FISH_AUDIO) {
    form.voice.settings = {
      model: '',
      latency: FishAudioLatency.NORMAL,
      speed: 1.0,
      volume: 0,
      normalize: false,
      chunkLength: 100,
    }
  }
})

watch(() => form.brain.platform, (newPlatform) => {
  if (newPlatform === BrainPlatform.OPENAI) {
    form.brain.settings = {
      model: 'gpt-4o-mini',
    }
  }
  else if (newPlatform === BrainPlatform.DOUBAO) {
    form.brain.settings = {
      model: 'ep-20240819205907-b9x2j',
    }
  }
  // else if (newPlatform === BrainPlatform.DIFY) {
  //   form.brain.settings = {
  //     apiKey: '',
  //     baseURL: 'https://sp-dify.sfo1.zeabur.app/v1',
  //   }
  // }
})

function beforeUpdate() {
  // if (!form.voice.platform) {
  //   ElMessage.error('请选择音色平台')
  //   return false
  // }
  // if (!form.voice.settings.voice_id) {
  //   ElMessage.error('请输入音色ID')
  //   return false
  // }
  return true
}

async function handleUpdate() {
  if (!form.id) {
    ElMessage.error('请先创建角色')
    return
  }

  if (!beforeUpdate()) {
    ElMessage.error('请检查输入')
    return
  }
  const res = await characterStore.updateCharecter(form)
  if (res) {
    ElMessage.error(res)
  }
  else {
    ElMessage.success('更新成功')
  }
}

function handleBack() {
  characterStore.setCurrentCharecter(null)
}

// 处理空间数据的更新
function handleSpatialDataUpdate(data: SpatialData) {
  form.spatialData = data
}
</script>

<template>
  <el-form :model="form" label-width="auto">
    <el-form-item label="角色名字">
      <el-input v-model="form.name" />
    </el-form-item>
    <el-form-item label="人设">
      <el-input v-model="form.description" type="textarea" />
    </el-form-item>
    <el-form-item label="角色额外信息">
      <el-input v-model="form.extra_info" type="textarea" />
    </el-form-item>

    <!-- 空间数据 -->
    <el-form-item label="模型文件">
      <SpatialDataUploader @update:data="handleSpatialDataUpdate" />
    </el-form-item>

    <el-form-item label="模型 settings.json">
      <SettingsEditor v-model:settings="form.model_settings_json" />
    </el-form-item>

    <!-- 语音相关的设置 -->
    <el-form-item label="音色平台">
      <el-select v-model="form.voice.platform" placeholder="请选择音色平台">
        <el-option v-for="value in voicePlatform" :key="value" :label="value" :value="value" />
      </el-select>
    </el-form-item>

    <div v-if="form.voice.platform === VoicePlatform.VOLC">
      <el-form-item label="音色ID">
        <el-input v-model="form.voice.settings.speaker" type="text" />
      </el-form-item>
      <el-form-item label="语速">
        <el-input
          v-model.number="form.voice.settings.speechRate" type="number" :min="-50" :max="100" :step="1"
          placeholder="请输入-50到100之间的数字"
        />
      </el-form-item>
      <el-form-item label="音高">
        <el-input
          v-model.number="form.voice.settings.pitchRate" type="number" :min="-12" :max="12" :step="1"
          placeholder="请输入-12到12之间的整数"
        />
      </el-form-item>
    </div>

    <div v-if="form.voice.platform === VoicePlatform.ELEVENLABS">
      <el-form-item label="音色ID">
        <el-input v-model="form.voice.settings.voiceId" type="text" />
      </el-form-item>
      <el-form-item label="音色模型选择">
        <el-input v-model="form.voice.settings.model" type="text" />
      </el-form-item>
      <el-form-item label="语音稳定性">
        <el-input
          v-model.number="form.voice.settings.similarityBoost" type="number" :min="0" :max="1" :step="0.05"
          placeholder="请输入0到1之间的数字"
        />
      </el-form-item>
      <el-form-item label="语音和文本的相似度（灵活性）">
        <el-input
          v-model.number="form.voice.settings.stability" type="number" :min="0" :max="1" :step="0.05"
          placeholder="请输入0到1之间的小数"
        />
      </el-form-item>
    </div>

    <div v-if="form.voice.platform === VoicePlatform.FISH_AUDIO">
      <el-form-item label="模型ID">
        <el-input v-model="form.voice.settings.model" type="text" />
      </el-form-item>
      <el-form-item label="chunkLength">
        <el-input
          v-model.number="form.voice.settings.chunkLength" type="number" :min="100" :max="300" :step="1"
          placeholder="请输入100到200之间的数字"
        />
      </el-form-item>
      <el-form-item label="语速">
        <el-input
          v-model.number="form.voice.settings.speed" type="number" :min="0.5" :max="2.0" :step="0.1"
          placeholder="请输入0.5到2.0之间的数字"
        />
      </el-form-item>
      <el-form-item label="音量">
        <el-input
          v-model.number="form.voice.settings.volume" type="number" :min="0.5" :max="2.0" :step="0.1"
          placeholder="请输入0.5到2.0之间的数字"
        />
      </el-form-item>
      <el-form-item label="数字标准化">
        <el-select v-model="form.voice.settings.normalize" placeholder="请选择是否标准化">
          <el-option :value="true" label="是" />
          <el-option :value="false" label="否" />
        </el-select>
      </el-form-item>

      <el-form-item label="模型模式(latency)">
        <el-select v-model="form.voice.settings.latency">
          <el-option :value="FishAudioLatency.NORMAL" label="正常" />
          <el-option :value="FishAudioLatency.BALANCED" label="balanced(减少延迟，但性能下降)" />
        </el-select>
      </el-form-item>
    </div>

    <!-- LLM相关的设置 -->
    <el-form-item label="AI平台">
      <el-select v-model="form.brain.platform" placeholder="请选择AI平台">
        <el-option v-for="value in brainPlatform" :key="value" :label="value" :value="value" />
      </el-select>
    </el-form-item>

    <el-form-item label="Driven Base URL">
      <el-input v-model="form.driven_base_url" type="text" />
    </el-form-item>

    <!-- <div v-if="form.brain.platform === BrainPlatform.DIFY">
      <el-form-item label="api key">
        <el-input v-model="form.brain.settings.apiKey" type="text" />
      </el-form-item>
      <el-form-item label="base url">
        <el-input v-model="form.brain.settings.baseURL" type="text" />
      </el-form-item>
    </div>  -->
    <el-form-item label="模型">
      <el-input v-model="form.brain.settings.model" type="text" />
    </el-form-item>

    <el-form-item label="欢迎语">
      <el-input v-model="form.greeting" type="textarea" />
    </el-form-item>
    <el-form-item label="欢迎语（第一次）">
      <el-input v-model="form.first_greeting" type="textarea" />
    </el-form-item>
    <el-form-item label="资源相关链接">
      <el-input v-model="form.resource" type="textarea" />
    </el-form-item>
    <el-form-item label="上线状态">
      <el-switch v-model="form.status" active-value="online" inactive-value="offline" />
    </el-form-item>
    <el-form-item>
      <el-button type="primary" @click="handleUpdate">
        更新
      </el-button>
      <el-button @click="handleBack">
        返回
      </el-button>
    </el-form-item>
  </el-form>
</template>
